import Api from '../Api'
import { INFTSalesListFilter } from '../../pages/NFTMarketPlace/models/INFTSalesList'
import { NFTSalesFilterHelper } from '../../pages/NFTMarketPlace/utils/NFTSalesFilterHelper'
import { NFT_ERC721_GRAPH_URL } from '../../constants/service'
import { MARKET_PLACE, USER_PROFILE } from '../../pages/NFTMarketPlace/data/NFTMarketPerPages'

async function BaseGraphql(queryString: string, url?: string) {
  return await Api.post(url ? url : '', { query: `{${queryString}}` })
}

async function getBlackList() {
  return await fetch('/blackList.json', {
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json'
    }
  }).then(result => result.json())
}

function getSalesNFTListFilterQuery(
  tokenBlackList: number[],
  sellerBlackList: string[],
  creatorBlackList: string[],
  filter?: INFTSalesListFilter,
  currency?: string
) {
  const creatorListJoin = creatorBlackList.join(',')
  let result = `creator_not_in: [${creatorListJoin}]`
  if (filter === undefined || (filter && filter.categoryId === null)) {
    result = `,
        category_not_in: [5],
        uniqueTokenId_not_in: [${tokenBlackList.join(',')}],
        seller_not_in: [${sellerBlackList.join(',')}],
        creator_not_in: [${creatorListJoin}]
        `
  } else if (filter && filter.categoryId === 5) {
    result = ''
  }

  if (currency && currency !== 'ALL') {
    result = `saleToken: "${currency}"`
  }

  return result
}

function getSalesNFTSortQuery(sortItems?: string) {
  let result = 'orderBy: insertDate, orderDirection: desc'

  if (sortItems) {
    if (sortItems === 'NewestItems') {
      result = 'orderBy: insertDate, orderDirection: desc'
    } else if (sortItems === 'OldestItems') {
      result = 'orderBy: insertDate, orderDirection: asc'
    } else if (sortItems === 'PriceLowToHigh') {
      result = 'orderBy: price, orderDirection: asc'
    } else if (sortItems === 'PriceHighToLow') {
      result = 'orderBy: price, orderDirection: desc'
    }
  }

  return result
}

function getSalesNotInNFTListFilterQuery(
  tokenBlackList: number[],
  sellerBlackList: string[],
  creatorBlackList: string[],
  filter?: INFTSalesListFilter
) {
  const creatorListJoin = creatorBlackList.join(',')
  let result = `creator_in: [${creatorListJoin}]`
  if (filter === undefined || (filter && filter.categoryId === null)) {
    result = `,
        creator_in: [${creatorListJoin}]
        `
  } else if (filter && filter.categoryId === 5) {
    result = ''
  }

  return result
}

export async function getSalesNFTList(
  filter?: INFTSalesListFilter,
  skip?: number,
  sortItems?: string,
  perPage?: number,
  currency?: string
) {
  const getFilterQuery = NFTSalesFilterHelper(filter)
  const getBlackListData = await getBlackList()
  const tokenBlackList = getBlackListData.token.map(
    (item: number) => `"0x9c39fb419556a472a12ce5835c8b4c28a7868257:${item}"`
  )

  const category = filter && filter.categoryId !== null ? `${filter.categoryId}` : 'All'
  const skipItems = skip ? skip : 0
  const currencyFilter = currency && currency !== 'ALL' ? `:${currency.toLowerCase()}` : ''

  const filterControl = getSalesNFTListFilterQuery(
    tokenBlackList,
    getBlackListData.seller,
    getBlackListData.creator,
    filter,
    currency
  )

  const nftListQuery = ` saleNFTs(
      where: {
        status: 1 ${getFilterQuery},
        ${filterControl}
      } 
      ${getSalesNFTSortQuery(sortItems)}
      first: ${perPage ? perPage : MARKET_PLACE.HOME}, skip: ${skipItems}
    ) {
    id
    tokenContract
    tokenId
    price
    category
    seller
    saleIndex
    marketContract
    saleToken
  }
   countObjects(where: { id: "ActiveSell${currencyFilter}:${category}" }) {
    count
  }
  `

  const result = await BaseGraphql(nftListQuery)
  const { saleNFTs, countObjects } = result.data
  return {
    data: saleNFTs,
    count: parseInt(countObjects[0].count)
  }
}

export async function getNotInSalesNFTList(filter?: INFTSalesListFilter) {
  const getFilterQuery = NFTSalesFilterHelper(filter)
  const getBlackListData = await getBlackList()
  const tokenBlackList = getBlackListData.token.map(
    (item: number) => `"0x9c39fb419556a472a12ce5835c8b4c28a7868257:${item}"`
  )
  const filterControl = getSalesNotInNFTListFilterQuery(
    tokenBlackList,
    getBlackListData.seller,
    getBlackListData.creator,
    filter
  )
  const nftListQuery = ` saleNFTs(
      where: {
        status: 1 ${getFilterQuery},
        ${filterControl}
      } 
      orderBy: insertDate, orderDirection: desc
      first: 1000
    ) {
    id
    tokenContract
    tokenId
    price
    category
    seller
    saleIndex
    marketContract
    saleToken
  }
  `

  const result = await BaseGraphql(nftListQuery)
  const { saleNFTs } = result.data
  return saleNFTs.length
}

export async function getNFTItem(tokenId: number, tokenContract: string) {
  const nftListQuery = ` saleNFTs(where: {tokenContract: "${tokenContract}" tokenId: ${tokenId} status: 1} orderBy: insertDate, orderDirection: desc) {
    id
    tokenContract
    tokenId
    price
    seller
    category
    status
    buyer
    saleToken
    saleIndex
    marketContract
  }`

  const result = await BaseGraphql(nftListQuery)
  return result.data.saleNFTs
}

export async function getNFTItemFromERC721(tokenId: number, tokenContract: string) {
  const nftListQuery = `  tokens(where: {contract: "${tokenContract.toLowerCase()}" tokenID:"${tokenId}"}) {
    id
    contract {
      id
    }
    tokenID
    tokenURI
    owner {
      id
    }
  }`

  const result = await BaseGraphql(nftListQuery, NFT_ERC721_GRAPH_URL)
  let tokenItem = undefined

  if (result && result.data && result.data.tokens && result.data.tokens.length > 0) {
    tokenItem = result.data.tokens[0]
  }

  return tokenItem
}

export async function getAccountOnSaleNFTItems(account: string) {
  const nftListQuery = ` saleNFTs(where: {seller: "${account}" status: 1} orderBy: insertDate, orderDirection: desc) {
    id
    tokenContract
    tokenId
    price
    seller
    category
    status
    buyer
    saleToken
    saleIndex
    marketContract
  }`

  const result = await BaseGraphql(nftListQuery)
  return result.data.saleNFTs
}

export async function getAccountCollectionNFTItems(account: string, contracts: string[]) {
  const nftListQuery = `  tokens(where: {owner: "${account.toLowerCase()}", contract_in: [${contracts.join(',')}]}) {
    id
    contract {
      id
    }
    tokenID
    tokenURI
    owner {
      id
    }
  }`

  const result = await BaseGraphql(nftListQuery, NFT_ERC721_GRAPH_URL)
  return result.data.tokens
}

export async function getAccountHistory(account: string, type: string, skip: number) {
  const actionFilter: string = type !== 'All' ? `action: "${type}"` : ''
  const userAccount = account.toLowerCase()
  const nftListQuery = ` histories(where: {account: "${userAccount}" ${actionFilter}} orderBy: timestamp, orderDirection: desc, first: ${USER_PROFILE.ACTIVITY}, skip: ${skip}) {
    id
    account
    action
    tokenContract
    tokenId
    opponent
    price
    saleToken
    timestamp
    transactionHash
  }
  countObjects(where: {id: "${userAccount}:History:${type}"}) {
    count
  }
`

  const result = await BaseGraphql(nftListQuery)
  const { histories, countObjects } = result.data

  return {
    data: histories,
    count: countObjects && countObjects.length > 0 ? parseInt(countObjects[0].count) : 0
  }
}
